import React, { useContext, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  Info,
  Link as LinkIcon,
  Mail,
  Printer,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import { createInvite, emailInvite } from "../../../services/CareTeamService";
import { apiErrorMessage, formatLongDate } from "../careUtils";
import { Dialog, icon } from "../ui";

const COPIED_MS = 1800;

const FormError = ({ text }) =>
  text ? (
    <p className="ct-form-error" role="alert">
      {icon(AlertTriangle)}
      <span>{text}</span>
    </p>
  ) : null;

const useCopy = () => {
  const [copied, setCopied] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // Clipboard can be blocked; the text is still on screen to copy by hand.
    }
    setCopied(key);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(null), COPIED_MS);
  };
  return [copied, copy];
};

const printInviteSheet = () => {
  document.body.classList.add("ct-printing");
  const done = () => {
    document.body.classList.remove("ct-printing");
    window.removeEventListener("afterprint", done);
  };
  window.addEventListener("afterprint", done);
  window.print();
};

const InviteForm = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    patient_name: "",
    email: "",
    year_of_birth: "",
    gentle_start: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.patient_name.trim()) {
      setError("Please enter the patient's name.");
      return;
    }
    const year = form.year_of_birth.trim();
    if (year && !/^\d{4}$/.test(year)) {
      setError("Please enter the year of birth, like 1951, or leave it blank.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      onCreated(
        await createInvite({
          patient_name: form.patient_name.trim(),
          email: form.email.trim(),
          year_of_birth: year ? Number(year) : null,
          gentle_start: form.gentle_start,
        }),
      );
    } catch (err) {
      setError(apiErrorMessage(err, "We couldn't create the invite. Please try again."));
      setBusy(false);
    }
  };

  return (
    <form className="ct-stack" onSubmit={create} noValidate>
      <p>You'll get a link, a QR code and a sheet to print.</p>
      <div className="ct-field">
        <label htmlFor="ct-invite-name">Patient's full name</label>
        <input
          id="ct-invite-name"
          className="ct-input"
          placeholder="e.g. Walter Brennan"
          value={form.patient_name}
          onChange={set("patient_name")}
        />
      </div>
      <div className="ct-two-col">
        <div className="ct-field">
          <label htmlFor="ct-invite-email">
            Email <span className="ct-optional">(optional)</span>
          </label>
          <input
            id="ct-invite-email"
            className="ct-input"
            type="email"
            placeholder="We'll email them the invite"
            value={form.email}
            onChange={set("email")}
          />
        </div>
        <div className="ct-field">
          <label htmlFor="ct-invite-year">
            Year of birth <span className="ct-optional">(optional)</span>
          </label>
          <input
            id="ct-invite-year"
            className="ct-input"
            inputMode="numeric"
            placeholder="e.g. 1951"
            maxLength={4}
            value={form.year_of_birth}
            onChange={set("year_of_birth")}
          />
        </div>
      </div>
      <label className="ct-check" style={{ fontSize: 15, color: "var(--color-text)" }}>
        <input type="checkbox" checked={form.gentle_start} onChange={set("gentle_start")} />
        Use large text and a slower pace for their first sessions
      </label>
      <FormError text={error} />
      <div className="ct-actions">
        <button type="button" className="ct-btn ct-btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="ct-btn ct-btn--primary" disabled={busy}>
          Create invite
        </button>
      </div>
    </form>
  );
};

// Email the invite, or try again after a failed send.
const EmailInvite = ({ invite, onSent }) => {
  const failed = !!invite.email && !invite.emailed_at;
  const [email, setEmail] = useState(invite.email || "");
  const [error, setError] = useState(
    failed
      ? `We couldn't email ${invite.email} just now. Share the link or the printed sheet, or try again.`
      : "",
  );
  const [busy, setBusy] = useState(false);

  if (invite.emailed_at) {
    return (
      <span className="ct-success" style={{ fontSize: 15 }}>
        {icon(Check)}
        Invite emailed to {invite.email}
      </span>
    );
  }

  const send = async () => {
    setError("");
    setBusy(true);
    try {
      onSent(await emailInvite(invite.id, email.trim()));
    } catch (err) {
      setError(apiErrorMessage(err, "We couldn't send the email. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ct-field">
      <label htmlFor="ct-invite-send">
        Email the invite <span className="ct-optional">(optional)</span>
      </label>
      <div className="ct-input-row">
        <input
          id="ct-invite-send"
          className="ct-input"
          type="email"
          placeholder="patient@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button
          type="button"
          className="ct-btn ct-btn--secondary"
          style={{ whiteSpace: "nowrap", fontSize: 15, padding: "0 14px" }}
          onClick={send}
          disabled={busy || !email.includes("@")}
        >
          {icon(Mail)}
          {failed ? "Try again" : "Send"}
        </button>
      </div>
      <FormError text={error} />
    </div>
  );
};

const InviteResult = ({ invite: created, onDone }) => {
  const [invite, setInvite] = useState(created);
  const [copied, copy] = useCopy();
  const { user } = useContext(AuthContext);
  const provider = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const until = formatLongDate(invite.expires_at);

  return (
    <div className="ct-stack ct-stack--lg">
      <div className="ct-invite-panel">
        <QRCodeSVG
          value={invite.link}
          size={132}
          className="ct-invite-qr"
          title={`QR code for ${invite.link}`}
        />
        <div className="ct-invite-panel__code">
          <span className="ct-code" data-testid="invite-code">
            {invite.code}
          </span>
          <span className="ct-muted" style={{ fontSize: 14 }}>
            Works until {until}
          </span>
          <button
            type="button"
            className="ct-btn ct-btn--ghost"
            onClick={() => copy(invite.code, "code")}
          >
            {icon(Copy)}
            {copied === "code" ? "Copied" : "Copy code"}
          </button>
        </div>
      </div>
      <div className="ct-field">
        <label htmlFor="ct-invite-link">Invite link</label>
        <div className="ct-input-row">
          <input
            id="ct-invite-link"
            className="ct-input"
            readOnly
            value={invite.link}
            style={{ fontSize: 15 }}
          />
          <button
            type="button"
            className="ct-btn ct-btn--secondary"
            style={{ whiteSpace: "nowrap", fontSize: 15, padding: "0 14px" }}
            onClick={() => copy(invite.link, "link")}
          >
            {icon(LinkIcon)}
            {copied === "link" ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
      <EmailInvite invite={invite} onSent={setInvite} />
      <p className="ct-info">
        {icon(Info)}
        <span>
          {invite.patient_name} shows as <strong>Pending</strong> in your roster until
          they open the link and agree to share.
        </span>
      </p>
      <div className="ct-actions">
        <button type="button" className="ct-btn ct-btn--secondary" onClick={printInviteSheet}>
          {icon(Printer)}
          Print invite sheet
        </button>
        <button
          type="button"
          className="ct-btn ct-btn--primary"
          style={{ padding: "0 22px" }}
          onClick={() => onDone(invite.patient_name)}
        >
          Done
        </button>
      </div>
      <div className="ct-print-sheet" aria-hidden="true">
        <h1>You're invited to MendGrove</h1>
        <p>
          Hello {invite.patient_name}. {provider || "Your provider"} would like to follow
          your progress in MendGrove, a set of gentle brain-training games.
        </p>
        <div className="ct-print-sheet__qr">
          <QRCodeSVG value={invite.link} size={220} />
        </div>
        <ol>
          <li>
            Scan the square code with your phone or tablet camera. Or type this address
            into your web browser: <strong>{invite.link}</strong>
          </li>
          <li>
            Create your account (or sign in if you already have one) and agree to
            share your progress.
          </li>
        </ol>
        <p>
          Your invite code: <strong>{invite.code}</strong>. It works until {until}.
        </p>
        <p>You can stop sharing at any time in Settings → My care team.</p>
      </div>
    </div>
  );
};

// One way to add a patient, whether or not they already play: an invite
// link they open to create an account or sign in, and agree to share.
const AddPatientModal = ({ onClose, onDone }) => {
  const [invite, setInvite] = useState(null);

  return (
    <Dialog onClose={onClose} labelledBy="ct-add-title">
      <div className="ct-dialog__head">
        <h2 id="ct-add-title" className="ct-dialog__title">
          {invite ? `Invite ready for ${invite.patient_name}` : "Add a patient"}
        </h2>
        <button
          type="button"
          className="ct-btn ct-btn--ghost ct-btn--icon"
          aria-label="Close"
          onClick={onClose}
        >
          {icon(X, 22)}
        </button>
      </div>
      {invite ? (
        <InviteResult invite={invite} onDone={onDone} />
      ) : (
        <InviteForm onClose={onClose} onCreated={setInvite} />
      )}
    </Dialog>
  );
};

export default AddPatientModal;
